import { InfoCircleOutlined } from '@ant-design/icons';
import { Popover as AntPopover, Spin, Switch } from 'antd';
import { useFormikContext } from 'formik';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ControlLabel, FormGroup, Input, InputNumber, Panel } from 'rsuite';

import { FormInput } from '../../../components/FormInputs/FormInput';
import { firebaseFunctions } from '../../../firebase/firebase';
import { FirebaseCallableFunctions } from '../../../firebase/firebase.models';
import { Offer, OfferCompanyDetails } from '../../../models/offers.model';

interface Props {
  submitted: boolean;
}

const FIELD_PREFIX = 'offerCompanyDetails';

export const CompanyDetailsSection: React.FC<Props> = ({ submitted }) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<Offer>();
  const [loading, setLoading] = useState(false);
  const [noKRS, setNoKRS] = useState(!values.offerCompanyDetails?.numerKRS);

  const fetchFromRegon = async (nip: string) => {
    setLoading(true);
    try {
      const callRegonAPI = firebaseFunctions.httpsCallable(FirebaseCallableFunctions.ON_CALL_REGON_API);
      const res = await callRegonAPI({ NIP: nip });
      if (res.data.status !== 'ERROR' && res.data.resp?.ErrorCode !== '4') {
        const resp = res.data.resp;
        const filled: Partial<OfferCompanyDetails> = {
          nip,
          nazwa: resp.Nazwa[0],
          regon: resp.Regon[0],
          kraj: 'POLSKA',
          kodPocztowy: resp.KodPocztowy[0],
          miejscowosc: resp.Miejscowosc[0].toUpperCase(),
          nrDomu: `${resp.NrNieruchomosci[0]}${resp.NrLokalu[0] ? ' / ' + resp.NrLokalu[0] : ''}`,
          ulica: resp.Ulica[0].toUpperCase(),
          numerKRS: '',
        };
        Object.entries(filled).forEach(([key, value]) => {
          setFieldValue(`${FIELD_PREFIX}.${key}`, value);
        });
      }
    } catch (e) {
      console.error('REGON API error:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleNipChange = (v: string | number) => {
    setFieldValue(`${FIELD_PREFIX}.nip`, v);
    const digits = String(v ?? '').replace(/\D/g, '');
    if (digits.length === 10) {
      fetchFromRegon(digits);
    }
  };

  const handleNoKRSChange = (checked: boolean) => {
    setNoKRS(checked);
    if (checked) {
      setFieldValue(`${FIELD_PREFIX}.numerKRS`, '');
    }
  };

  return (
    <Panel header={t('OFFER.COMPANY_DATA_HEADING')} bordered className="mt-6">
      <FormGroup>
        <ControlLabel>{t('FORMS.CONNECT_COMPANY.NIP')}</ControlLabel>
        <div className="flex gap-2 items-center">
          <InputNumber
            value={values.offerCompanyDetails?.nip ?? ''}
            onChange={handleNipChange}
            className="w-40"
            controls={false}
            disabled={loading}
          />
          {loading && <Spin size="small" />}
        </div>
      </FormGroup>

      <div className="flex items-center gap-2 mb-4 mt-4">
        <Switch checked={noKRS} onChange={handleNoKRSChange} size="small" />
        <span className="text-sm">{t('VALIDATION.NO_KRS')}</span>
        <AntPopover
          trigger="hover"
          content={
            <div className="font-semibold w-96">
              {t('KRS_POPOVER_1')}
              <br />
              <br />
              {t('KRS_POPOVER_2')}
            </div>
          }>
          <InfoCircleOutlined className="ml-1" />
        </AntPopover>
      </div>

      {!noKRS && (
        <FormInput
          submitted={submitted}
          label={t('FORMS.CONNECT_COMPANY.KRS')}
          name={`${FIELD_PREFIX}.numerKRS`}
          inputAccepter={Input}
        />
      )}

      <FormInput
        submitted={submitted}
        label={t('FORMS.CONNECT_COMPANY.REGON')}
        name={`${FIELD_PREFIX}.regon`}
        inputAccepter={Input}
      />

      <FormInput
        submitted={submitted}
        label={t('FORMS.CONNECT_COMPANY.COMPANY_NAME')}
        name={`${FIELD_PREFIX}.nazwa`}
        inputAccepter={Input}
      />

      <FormInput
        submitted={submitted}
        label={t('FORMS.CONNECT_COMPANY.COUNTRY')}
        name={`${FIELD_PREFIX}.kraj`}
        inputAccepter={Input}
      />

      <FormInput
        submitted={submitted}
        label={t('FORMS.CONNECT_COMPANY.POSTAL_CODE')}
        name={`${FIELD_PREFIX}.kodPocztowy`}
        inputAccepter={Input}
      />

      <FormInput
        submitted={submitted}
        label={t('FORMS.CONNECT_COMPANY.CITY')}
        name={`${FIELD_PREFIX}.miejscowosc`}
        inputAccepter={Input}
      />

      <FormInput
        submitted={submitted}
        label={t('FORMS.CONNECT_COMPANY.STREET')}
        name={`${FIELD_PREFIX}.ulica`}
        inputAccepter={Input}
      />

      <FormInput
        submitted={submitted}
        label={t('FORMS.CONNECT_COMPANY.HOUSE_NUMBER')}
        name={`${FIELD_PREFIX}.nrDomu`}
        inputAccepter={Input}
      />

      <FormInput
        submitted={submitted}
        label={t('BANK_ACCOUNT_NR')}
        name={`${FIELD_PREFIX}.bankAccount`}
        inputAccepter={Input}
        placeholder="00 0000 0000 0000 0000 0000 0000"
      />
    </Panel>
  );
};
